import { logger } from '@/lib/logger';
import React, { useState } from 'react';
import { db } from '@/lib/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

export default function EscalationForm() {
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        companyUrl: '',
        reportedBy: '',
        escalationFrom: 'Sales Team', // Sales Team or PM Team
        conflictReason: '',
        evidence: [] // URLs
    });

    // Get companyId from URL
    const searchParams = new URLSearchParams(window.location.search);
    const companyId = searchParams.get('cid');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const caseId = `ESC-P-${Date.now().toString().slice(-6)}`;
            await addDoc(collection(db, 'escalation_cases'), {
                ...formData,
                caseId, // Generate ID for public forms too
                companyId: companyId || 'unknown', // Critical for scoping
                type: 'Internal', // Default
                status: 'Open',
                createdAt: serverTimestamp(),
                isInvalid: false,
                source: 'Public Form'
            });
            setSubmitted(true);
        } catch (error) {
            logger.error("Error submitting escalation:", error);
            alert("Failed to submit escalation. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            // Use public signature endpoint for public form
            const signReq = await fetch('/api/public-cloudinary-sign', {
                method: 'POST'
            });
            if (!signReq.ok) throw new Error('Failed to get secure public upload signature');
            const { signature, timestamp, preset } = await signReq.json();

            const data = new FormData();
            data.append('file', file);
            data.append('upload_preset', preset || 'oblique_uploads');
            data.append('timestamp', timestamp);
            data.append('signature', signature);
            data.append('api_key', import.meta.env.VITE_CLOUDINARY_API_KEY);

            const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
            const res = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
                method: 'POST',
                body: data
            });
            const urlData = await res.json();
            if (urlData.secure_url) {
                setFormData(prev => ({
                    ...prev,
                    evidence: [...prev.evidence, urlData.secure_url]
                }));
            }
        } catch (err) {
            logger.error('Upload failed', err);
            alert('Upload failed');
        }
    };

    if (submitted) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
                <div style={{ textAlign: 'center', maxWidth: '400px', padding: '40px' }}>
                    <div style={{ width: '80px', height: '80px', background: 'var(--success-subtle)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                        <ObliqueIcon name="check_circle" size={40} color="var(--success)" />
                    </div>
                    <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px', color: 'var(--text-primary)' }}>Escalation Submitted</h1>
                    <p style={{ color: 'var(--text-secondary)', marginBottom: '32px', lineHeight: '1.6' }}>
                        Your case has been logged successfully. The Quality Team has been notified and will review the case shortly.
                    </p>
                    <button
                        onClick={() => { setSubmitted(false); setFormData({ companyUrl: '', reportedBy: '', escalationFrom: 'Sales Team', conflictReason: '', evidence: [] }); }}
                        className="btn btn-primary"
                    >
                        Submit Another Case
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', background: '#f9fafb', padding: '40px 20px', display: 'flex', justifyContent: 'center' }}>
            <div className="card" style={{ maxWidth: '600px', width: '100%', padding: '40px', background: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <div style={{ marginBottom: '32px', textAlign: 'center' }}>
                    <h1 style={{ fontSize: '24px', fontWeight: '500', color: 'var(--text-primary)', marginBottom: '8px' }}>Create Escalation Case</h1>
                    <p style={{ color: 'var(--text-secondary)' }}>Report a conflict or issue for Quality Assurance review.</p>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

                    <div>
                        <label className="label" style={{ marginBottom: '8px', display: 'block' }}>Company Name / URL *</label>
                        <input
                            required
                            className="input"
                            placeholder="e.g. company.com"
                            value={formData.companyUrl}
                            onChange={e => setFormData({ ...formData, companyUrl: e.target.value })}
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div>
                            <label className="label" style={{ marginBottom: '8px', display: 'block' }}>Escalation From *</label>
                            <div style={{ display: 'flex', background: 'var(--bg-subtle)', padding: '4px', borderRadius: '8px' }}>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, escalationFrom: 'Sales Team' })}
                                    style={{
                                        flex: 1, padding: '10px', borderRadius: '6px', border: 'none', fontWeight: '500', fontSize: '13px', cursor: 'pointer',
                                        background: formData.escalationFrom === 'Sales Team' ? '#fff' : 'transparent',
                                        color: formData.escalationFrom === 'Sales Team' ? 'var(--accent)' : 'var(--text-secondary)',
                                        boxShadow: formData.escalationFrom === 'Sales Team' ? '0 2px 8px rgba(0,0,0,0.05)' : 'none'
                                    }}
                                >
                                    Sales Team
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, escalationFrom: 'PM Team' })}
                                    style={{
                                        flex: 1, padding: '10px', borderRadius: '6px', border: 'none', fontWeight: '500', fontSize: '13px', cursor: 'pointer',
                                        background: formData.escalationFrom === 'PM Team' ? '#fff' : 'transparent',
                                        color: formData.escalationFrom === 'PM Team' ? 'var(--purple)' : 'var(--text-secondary)',
                                        boxShadow: formData.escalationFrom === 'PM Team' ? '0 2px 8px rgba(0,0,0,0.05)' : 'none'
                                    }}
                                >
                                    PM Team
                                </button>
                            </div>
                        </div>
                        <div>
                            <label className="label" style={{ marginBottom: '8px', display: 'block' }}>Reported By (Name) *</label>
                            <input
                                required
                                className="input"
                                placeholder="e.g. John Smith"
                                value={formData.reportedBy}
                                onChange={e => setFormData({ ...formData, reportedBy: e.target.value })}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="label" style={{ marginBottom: '8px', display: 'block' }}>Conflict Reason / Description *</label>
                        <textarea
                            required
                            className="input"
                            rows={6}
                            placeholder="Describe the issue in detail..."
                            value={formData.conflictReason}
                            onChange={e => setFormData({ ...formData, conflictReason: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="label" style={{ marginBottom: '8px', display: 'block' }}>Evidence (Screenshots/Files)</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                            {formData.evidence.map((url, i) => (
                                <div key={i} style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--border)' }}>
                                    <img src={url} alt="Evidence" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, evidence: prev.evidence.filter((_, idx) => idx !== i) }))}
                                        style={{ position: 'absolute', top: '2px', right: '2px', background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                    >
                                        <ObliqueIcon name="close" size={12} />
                                    </button>
                                </div>
                            ))}
                            <label style={{
                                width: '80px', height: '80px', borderRadius: '8px', border: '1px dashed var(--border)',
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', background: 'var(--bg-subtle)', color: 'var(--text-tertiary)'
                            }}>
                                <ObliqueIcon name="upload" size={20} />
                                <span style={{ fontSize: '10px', marginTop: '4px' }}>Upload</span>
                                <input type="file" accept="image/*" onChange={handleImageUpload} hidden />
                            </label>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="btn btn-primary"
                        style={{ padding: '16px', fontSize: '16px', marginTop: '16px' }}
                    >
                        {loading ? 'Submitting...' : 'Submit Escalation Case'}
                    </button>

                </form>
            </div>
        </div>
    );
}
